import React, { Dispatch, SetStateAction, useContext } from 'react'
import {
  GeckoAssetModel,
  GeckoColorScaleThreshold,
  GeckoComponentDataFrame,
  GeckoComponentShape,
  GeckoFeature,
  GeckoMarkupLayer,
  GeckoMarkupPrimitive,
  GeckoMeasurementRange,
} from 'types'

import {
  AssetVizController,
  AssetVizMode,
  AssetVizSceneConfig,
} from './defines'
import { HtmlOverlayEntry, MouseState, Vec3 } from './utils/helpers'

export type AssetVizContextType = {
  // Backing data
  assetVizDom: React.RefObject<HTMLDivElement>
  assetModel: GeckoAssetModel | undefined
  focusedComponentID: string
  focusedCDF: GeckoComponentDataFrame | undefined
  getColorThresholdForComponent: (
    cdf: GeckoComponentDataFrame
  ) => Promise<GeckoColorScaleThreshold | undefined>
  markupLayers: ReadonlyArray<GeckoMarkupLayer>
  markupLayerLabelVisibility: ReadonlyArray<string>
  componentDataFrames: ReadonlyArray<GeckoComponentDataFrame>
  // Styling
  assetColorString: string
  weldColorString: string
  corrosionRegionColorString: string
  featureLabelColorString: string
  gridColorString: string
  draftSelectionColorString: string
  selectionColorString: string
  backgroundColorString: string
  labelBackgroundColorString: string
  setShowDataLoadingDisplay: (show: boolean) => void
  // View controls
  mode: AssetVizMode
  showMultiComponentViewer: boolean
  userIsNavigating: boolean
  setUserIsNavigating: Dispatch<SetStateAction<boolean>>
  offscreenRender?: boolean
  // The third value is a counter that is active while the viewport is animating
  viewportSizeDuringAnimation: React.RefObject<Vec3>
  cameraFitMultiplier?: number
  draftSelection:
    | GeckoComponentShape
    | GeckoMarkupPrimitive
    | GeckoFeature
    | undefined
  selection:
    | GeckoComponentShape
    | GeckoMarkupPrimitive
    | GeckoMarkupLayer
    | GeckoFeature
    | undefined
  cdfToFilteredValueRange: (
    cdf: GeckoComponentDataFrame
  ) => GeckoMeasurementRange | undefined
  cdfToSelectedCategoricalValues: (
    cdf: GeckoComponentDataFrame
  ) => number[] | undefined
  onClicked: (event: MouseState | undefined) => void
  onHovered: (event: MouseState | undefined) => void
  onWrite: (selection: GeckoMarkupPrimitive) => Promise<void>
  refreshAndSelectLayer: (markupLayer: GeckoMarkupLayer) => Promise<void>
  deletePrimitive: (
    primitive: GeckoMarkupPrimitive,
    markupLayer: GeckoMarkupLayer
  ) => Promise<void>
  // Internal properties
  textureLoading: boolean
  setTextureLoading: Dispatch<SetStateAction<boolean>>
  setController: Dispatch<SetStateAction<AssetVizController>>
  htmlOverlaysRef: { current: ReadonlyArray<HtmlOverlayEntry> }
  setHtmlOverlays: Dispatch<SetStateAction<HtmlOverlayEntry[]>>
  //markup editing
} & AssetVizSceneConfig

const AssetVizContext = React.createContext<AssetVizContextType | undefined>(
  undefined
)

export const AssetVizInternalProvider: React.FC<{
  value: AssetVizContextType
  children: React.ReactNode
}> = ({ value, children }) => (
  <AssetVizContext.Provider value={value}>{children}</AssetVizContext.Provider>
)

export const useAssetVizInternal = () => {
  const context = useContext(AssetVizContext)
  if (!context)
    throw new Error('useAssetVizInternal() must be used within AssetViz')
  return context
}
